import {
  type MatchmakerInviteCandidateState,
  type MatchmakerInviteOpenState,
} from './MatchmakerService';
import { type SuperlikeSendStatus } from './SuperlikesService';

interface AnalyticsEvent {
  [key: string]: string | number | boolean;
}

export type MatchmakerMode = 'dating' | 'matchmaker';

export type MatchmakerShareVia = 'prepared' | 'share_url' | 'none';

export type SignupRole = 'dating' | 'matchmaker' | 'both';

export type SignUpStepEvent =
  | 'shown'
  | 'layout'
  | 'role_tapped'
  | 'next_pressed'
  | 'valid'
  | 'invalid'
  | 'upload_failed'
  | 'register_failed'
  | 'js_error';

export type MatchmakerInviteOpenGoalState =
  | MatchmakerInviteOpenState
  | 'feature_disabled';

export type MatchmakerInviteLandingGoalState =
  | MatchmakerInviteCandidateState
  | 'feature_disabled';

declare global {
  interface Window {
    ym?: (
      counterId: number,
      action: string,
      eventName: string,
      parameters?: AnalyticsEvent,
    ) => void;
  }
}

class AnalyticsService {
  private counterId = 100644700;

  private track(eventName: string, parameters: AnalyticsEvent = {}) {
    try {
      if (typeof window !== 'undefined' && window.ym) {
        window.ym(this.counterId, 'reachGoal', eventName, parameters);
      }
    } catch {
      void 0;
    }

    this.postGoal(eventName, parameters);
  }

  private postGoal(eventName: string, parameters: AnalyticsEvent) {
    try {
      Promise.all([
        import('@common/services/tmaInitDataService'),
        import('@common/utils/fetcher'),
      ])
        .then(async ([{ waitForTmaInitData }, { fetcher }]) => {
          const initData = await waitForTmaInitData();

          if (!initData) return;

          await fetcher({
            url: '/tracking/ym-goal',
            method: 'POST',
            data: {
              goal: eventName,
              params: parameters,
              initData,
            },
            auth: null,
          });
        })
        .catch(() => undefined);
    } catch {
      void 0;
    }
  }

  trackSignUpStarted(step: string = 'initial') {
    const referral =
      (typeof sessionStorage !== 'undefined' &&
        sessionStorage.getItem('referral_code')) ||
      'organic';
    this.track('sign_up_started', { step, referral_code: referral });
  }

  trackSignUpCompleted(source: string = 'direct') {
    const referral =
      (typeof sessionStorage !== 'undefined' &&
        sessionStorage.getItem('referral_code')) ||
      'organic';
    this.track('sign_up_completed', { source, referral_code: referral });
  }

  trackVerificationStarted(method: string = 'photo') {
    this.track('verification_started', { method });
  }

  trackVerificationSubmitted(method: string = 'photo') {
    this.track('verification_submitted', { method });
  }

  trackFeedLoaded(resultCount: number, loadTime?: number) {
    this.track('feed_loaded', {
      result_count: resultCount,
      load_time_ms: loadTime || 0,
    });
  }

  trackFeedEmpty(filterApplied: boolean = false) {
    this.track('feed_empty', { filter_applied: filterApplied });
  }

  trackSwipeLike(position: number) {
    this.track('swipe_like', { position });
  }

  trackSwipePass(position: number) {
    this.track('swipe_pass', { position });
  }

  trackLikesOpened() {
    this.track('likes_opened');
  }

  trackTasksOpened() {
    this.track('tasks_opened');
  }

  trackChatOpened() {
    this.track('chat_opened');
  }

  trackMessageSendClicked() {
    this.track('message_send_clicked');
  }

  trackMessageSendSuccess() {
    this.track('message_send_success');
  }

  trackMessageSendError(errorType: string = 'unknown') {
    this.track('message_send_error', { error_type: errorType });
  }

  trackPaywallViewed(source: string = 'organic') {
    this.track('paywall_viewed', { source });
  }

  trackPlanSelected(planType: string, planDuration?: string) {
    this.track(`plan_selected_${planType}`, {
      duration: planDuration || 'unknown',
    });
  }

  trackCheckoutInitiated(planType: string) {
    this.track('checkout_initiated', { plan_type: planType });
  }

  trackPaymentSuccess(planType: string, amount?: number) {
    this.track('payment_success', {
      plan_type: planType,
      amount: amount || 0,
    });
  }

  trackPaymentFailed(planType: string, errorType: string = 'unknown') {
    this.track('payment_failed', {
      plan_type: planType,
      error_type: errorType,
    });
  }

  trackTaskListViewed() {
    this.track('task_list_viewed');
  }

  trackTaskShown(taskType: string, position?: number) {
    this.track('task_shown', {
      task_type: taskType,
      position: position || 0,
    });
  }

  trackTaskClicked(taskType: string) {
    this.track('task_clicked', { task_type: taskType });
  }

  trackTaskRewarded(taskType: string, rewardType: string = 'unknown') {
    this.track('task_rewarded', {
      task_type: taskType,
      reward_type: rewardType,
    });
  }

  trackSuperlikeOpen() {
    this.track('superlike_open');
  }

  trackSuperlikeSent(status: SuperlikeSendStatus, hasMessage: boolean) {
    this.track('superlike_sent', { status, has_message: hasMessage });
  }

  trackSuperlikeLimitHit() {
    this.track('superlike_limit_hit');
  }

  trackSuperlikeIncomingShown(count: number) {
    this.track('superlike_incoming_shown', { count });
  }

  trackMmModeSwitched(mode: MatchmakerMode) {
    this.track('mm_mode_switched', { mode });
  }

  trackMmFriendSet() {
    this.track('mm_friend_set');
  }

  trackMmSkip() {
    this.track('mm_skip');
  }

  trackMmRecommendSent(hasNote: boolean) {
    this.track('mm_recommend_sent', { has_note: hasNote });
  }

  trackMmShare(via: MatchmakerShareVia) {
    this.track('mm_share', { via });
  }

  trackMmInviteOpened(state: MatchmakerInviteOpenGoalState) {
    this.track('mm_invite_opened', { state });
  }

  trackMmInviteLanding(state: MatchmakerInviteLandingGoalState) {
    this.track('mm_invite_landing', { state });
  }

  trackSignupRoleSelected(role: SignupRole) {
    this.track('signup_role_selected', { role });
  }

  /**
   * Where a sign-up stops. Ends in the server's log, not in Metrica. Names of
   * steps and fields only, never what the user typed.
   */
  trackSignUpStep(event: SignUpStepEvent, params: AnalyticsEvent = {}) {
    this.postGoal('sign_up_step', { event, ...params });
  }
}

export const analytics = new AnalyticsService();
